import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { BeneficiaryDesignationsData, Beneficiary, beneficiaryDesignationsService } from '../../../../services/beneficiaryDesignationsInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('BeneficiaryDesignationsData');

type BeneficiaryDesignationsDataScreenRouteProp = RouteProp<RootStackParamList, 'BeneficiaryDesignationsData'>;

export const BeneficiaryDesignationsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<BeneficiaryDesignationsDataScreenRouteProp>();
  const [designationsInfo, setDesignationsInfo] = useState<BeneficiaryDesignationsData | null>(null);
  const [loading, setLoading] = useState(true);

  const beneficiaryDesignationsId = route.params?.beneficiaryDesignationsId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly;

  const loadDesignationsInfo = useCallback(async () => {
    try {
      setLoading(true);
      logger.debug('Loading beneficiary designations info, id:', beneficiaryDesignationsId);
      if (beneficiaryDesignationsId) {
        const info = await beneficiaryDesignationsService.getBeneficiaryDesignationsById(beneficiaryDesignationsId, targetUserId);
        logger.debug('Loaded by ID:', JSON.stringify(info, null, 2));
        setDesignationsInfo(info);
      } else {
        const allInfo = await beneficiaryDesignationsService.getAllBeneficiaryDesignations(targetUserId);
        logger.debug('Loaded all:', JSON.stringify(allInfo, null, 2));
        if (allInfo.length > 0) {
          setDesignationsInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading beneficiary designations info:', error);
    } finally {
      setLoading(false);
    }
  }, [beneficiaryDesignationsId, targetUserId]);

  useFocusEffect(
    useCallback(() => {
      loadDesignationsInfo();
    }, [loadDesignationsInfo])
  );

  const handleEdit = () => {
    if (designationsInfo) {
      navigation.navigate('BeneficiaryDesignationsInput', {
        beneficiaryDesignationsData: designationsInfo,
        isEditing: true
      });
    } else {
      navigation.navigate('BeneficiaryDesignationsInput');
    }
  };

  const handleDelete = () => {
    if (designationsInfo) {
      Alert.alert(
        'Delete Beneficiary Designations',
        'Are you sure you want to delete this information? This action cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await beneficiaryDesignationsService.deleteBeneficiaryDesignations(designationsInfo.id!);
                const remainingData = await beneficiaryDesignationsService.getAllBeneficiaryDesignations();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('beneficiary_designations');
                }
                (navigation as any).navigate('EstatePlanning', { screen: 'EstatePlanningChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting beneficiary designations info:', error);
                Alert.alert('Error', 'Failed to delete beneficiary designations');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('EstatePlanning', { screen: 'EstatePlanningChecklist', params: { refreshKey: Date.now() } });
  };

  const renderBeneficiarySection = (beneficiaries: Beneficiary[] | undefined, title: string) => {
    if (!beneficiaries || beneficiaries.length === 0) return null;
    return (
      <View style={styles.beneficiarySection}>
        <Text style={styles.sectionTitle}>{title}</Text>
        {beneficiaries.map((b, index) => (
          <View key={index} style={styles.beneficiaryCard}>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Name</Text>
              <Text style={styles.value}>{b.name}</Text>
            </View>
            {b.relationship && (
              <View style={styles.infoItem}>
                <Text style={styles.label}>Relationship</Text>
                <Text style={styles.value}>{b.relationship}</Text>
              </View>
            )}
            {b.percentage && (
              <View style={styles.infoItem}>
                <Text style={styles.label}>Percentage</Text>
                <Text style={styles.value}>{b.percentage}%</Text>
              </View>
            )}
            {b.policyId && (
              <View style={styles.infoItem}>
                <Text style={styles.label}>Policy/Account ID</Text>
                <Text style={styles.value}>{b.policyId}</Text>
              </View>
            )}
            {b.bankName && (
              <View style={styles.infoItem}>
                <Text style={styles.label}>Institution</Text>
                <Text style={styles.value}>{b.bankName}</Text>
              </View>
            )}
          </View>
        ))}
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={require('../../../../assets/icons/back.png')} style={[styles.backIcon, { tintColor: '#fff' }]} />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image source={require('../../../../assets/icons/keyimage.png')} style={styles.backgroundKey} />
        <Image source={require('../../../../assets/logo.png')} style={styles.logo} resizeMode="contain" />
        <Text style={styles.title}>Beneficiary Designations</Text>
      </View>

      <View style={styles.infoContainer}>
        <ScrollView style={styles.scrollView}>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#29BFC0" />
              <Text style={styles.loadingText}>Loading...</Text>
            </View>
          ) : !designationsInfo ? (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No Beneficiary Designations data found</Text>
              <TouchableOpacity
                style={[styles.button, styles.editButton, { marginTop: 20 }]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>Add Beneficiary Details</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              {renderBeneficiarySection(designationsInfo?.lifeInsuranceBeneficiaries, 'Life Insurance')}
              {renderBeneficiarySection(designationsInfo?.retirementAccountBeneficiaries, 'Retirement Accounts')}
              {renderBeneficiarySection(designationsInfo?.bankAccountBeneficiaries, 'Bank Accounts')}
              {renderBeneficiarySection(designationsInfo?.investmentAccountBeneficiaries, 'Investment Accounts')}
              {renderBeneficiarySection(designationsInfo?.otherBeneficiaries, 'Other Assets')}

              {(designationsInfo?.lastReviewedDate || designationsInfo?.additionalNotes) && (
                <View style={styles.beneficiarySection}>
                  <Text style={styles.sectionTitle}>Additional Information</Text>
                  {designationsInfo?.lastReviewedDate && (
                    <View style={styles.infoItem}>
                      <Text style={styles.label}>Last Reviewed</Text>
                      <Text style={styles.value}>{designationsInfo.lastReviewedDate}</Text>
                    </View>
                  )}
                  {designationsInfo?.additionalNotes && (
                    <View style={styles.infoItem}>
                      <Text style={styles.label}>Notes</Text>
                      <Text style={styles.value}>{designationsInfo.additionalNotes}</Text>
                    </View>
                  )}
                </View>
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <TouchableOpacity
                    style={[styles.button, styles.deleteButton]}
                    onPress={handleDelete}
                  >
                    <Text style={styles.buttonText}>Delete</Text>
                  </TouchableOpacity>
                )}

                {!viewOnly && (
                  <TouchableOpacity
                    style={[styles.button, styles.editButton]}
                    onPress={handleEdit}
                  >
                    <Text style={styles.buttonText}>Edit</Text>
                  </TouchableOpacity>
                )}
              </View>
            </>
          )}

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#29BFC0' },
  header: { flexDirection: 'row', alignItems: 'center', padding: 15, paddingTop: 25, position: 'relative' },
  backButton: { position: 'absolute', left: 50, padding: 15, paddingTop: 50, zIndex: 1 },
  backIcon: { width: 32, height: 32, tintColor: '#fff' },
  topSection: { alignItems: 'center', paddingVertical: 20 },
  backgroundKey: { position: 'absolute', width: '85%', height: '85%', opacity: 0.1 },
  logo: { width: 125, height: 125, marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 'bold', color: '#FFFFFF', marginBottom: 8, paddingHorizontal: 30, textAlign: 'center' },
  infoContainer: { flex: 1, backgroundColor: '#F3F6F8', borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingTop: 20 },
  scrollView: { paddingHorizontal: 16, paddingBottom: 120 },
  beneficiarySection: { marginBottom: 20 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: '#1C2C4F', marginBottom: 16, marginTop: 10 },
  beneficiaryCard: { backgroundColor: '#FFFFFF', borderRadius: 12, padding: 16, marginBottom: 12, borderWidth: 1, borderColor: '#E5E5E5' },
  infoItem: { marginBottom: 8 },
  label: { fontSize: 14, fontWeight: '600', color: '#6B7280', marginBottom: 2 },
  value: { fontSize: 16, color: '#1C2C4F' },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: { height: 120 },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: ms(50),
  },
  loadingText: {
    marginTop: ms(10),
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
});
